import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const LoginPage = React.lazy(() => import('../pages/auth/LoginPage'));
const DashboardPage = React.lazy(() => import('../pages/dashboard/DashboardPage'));
const ClientDashboard = React.lazy(() => import('../pages/dashboard/ClientDashboard'));
const TasksPage = React.lazy(() => import('../pages/tasks/TasksPage'));
const TaskDetailPage = React.lazy(() => import('../pages/tasks/TaskDetailPage'));
const RecurrencesPage = React.lazy(() => import('../pages/tasks/RecurrencesPage'));
const ProjectsPage = React.lazy(() => import('../pages/projects/ProjectsPage'));
const ProjectDetailPage = React.lazy(() => import('../pages/projects/ProjectDetailPage'));
const CompaniesPage = React.lazy(() => import('../pages/companies/CompaniesPage'));
const UsersPage = React.lazy(() => import('../pages/users/UsersPage'));
const CalendarPage = React.lazy(() => import('../pages/calendar/CalendarPage'));
const SettingsPage = React.lazy(() => import('../pages/settings/SettingsPage'));
const HoursRegisterPage = React.lazy(() => import('../pages/horas/HoursRegisterPage'));
const HoursDashboardPage = React.lazy(() => import('../pages/horas/HoursDashboardPage'));
const HoursAdminPage = React.lazy(() => import('../pages/horas/HoursAdminPage'));
const MapasAtuacaoPage = React.lazy(() => import('../pages/mapaAtuacao/MapasAtuacaoPage'));
const MapaAtuacaoDetailPage = React.lazy(() => import('../pages/mapaAtuacao/MapaAtuacaoDetailPage'));
const PlanoAcaoPage = React.lazy(() => import('../pages/planoAcao/PlanoAcaoPage'));
const PlanoAcaoHomePage = React.lazy(() => import('../pages/planoAcao/PlanoAcaoHomePage'));
const TvPage = React.lazy(() => import('../pages/tasks/TvPage'));
const PromptsPage = React.lazy(() => import('../pages/prompts/PromptsPage'));

const PrivateRoute: React.FC<{ children: React.ReactElement; allowedRoles?: string[] }> = ({ 
  children, 
  allowedRoles 
}) => {
  const { token, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--surface-primary)',
        color: 'var(--color-text-primary)',
        fontSize: 'var(--font-size-lg)'
      }}>
        Carregando...
      </div>
    );
  }
  
  if (!token) {
    return <Navigate to="/login" />;
  }

  if (allowedRoles && user && !allowedRoles.includes(user.permissao)) {
    return <Navigate to="/" />;
  }
  
  return children;
};

const DashboardRouter: React.FC = () => {
  const { user } = useAuth();
  
  if (user?.permissao === 'cliente') {
    return <ClientDashboard />;
  }
  
  return <DashboardPage />;
};

const AppRoutes: React.FC = () => {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <React.Suspense fallback={
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--surface-primary)',
          color: 'var(--color-text-primary)',
          fontSize: 'var(--font-size-lg)'
        }}>
          Carregando...
        </div>
      }>
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          {/* Rota pública TV — sem autenticação JWT */}
          <Route path="/tv" element={<TvPage />} />
          
          <Route
            path="/"
            element={
              <PrivateRoute>
                <DashboardRouter />
              </PrivateRoute>
            }
          />
          
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <DashboardRouter />
              </PrivateRoute>
            }
          />
          
          <Route
            path="/tasks"
            element={
              <PrivateRoute>
                <TasksPage />
              </PrivateRoute>
            }
          />
          
          <Route
            path="/tasks/recorrencias"
            element={
              <PrivateRoute>
                <RecurrencesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/tasks/:id"
            element={
              <PrivateRoute>
                <TaskDetailPage />
              </PrivateRoute>
            }
          />
          
          <Route
            path="/projects"
            element={
              <PrivateRoute>
                <ProjectsPage />
              </PrivateRoute>
            }
          />
          
          <Route
            path="/projects/:id"
            element={
              <PrivateRoute>
                <ProjectDetailPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/mapa-atuacao"
            element={
              <PrivateRoute>
                <MapasAtuacaoPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/mapa-atuacao/projeto/:projetoId"
            element={
              <PrivateRoute>
                <MapaAtuacaoDetailPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/plano-acao"
            element={
              <PrivateRoute>
                <PlanoAcaoHomePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/plano-acao/detalhe"
            element={
              <PrivateRoute>
                <PlanoAcaoPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/prompts"
            element={
              <PrivateRoute>
                <PromptsPage />
              </PrivateRoute>
            }
          />
          
          <Route
            path="/companies"
            element={
              <PrivateRoute allowedRoles={['admin', 'gestor']}>
                <CompaniesPage />
              </PrivateRoute>
            }
          />
          
          <Route
            path="/users"
            element={
              <PrivateRoute allowedRoles={['admin', 'gestor']}>
                <UsersPage />
              </PrivateRoute>
            }
          />
          
          <Route
            path="/calendar"
            element={
              <PrivateRoute>
                <CalendarPage />
              </PrivateRoute>
            }
          />
          
          <Route
            path="/horas"
            element={
              <PrivateRoute allowedRoles={['admin', 'gestor', 'colaborador']}>
                <HoursRegisterPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/horas/dashboard"
            element={
              <PrivateRoute allowedRoles={['admin', 'gestor', 'colaborador']}>
                <HoursDashboardPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/horas/admin"
            element={
              <PrivateRoute allowedRoles={['admin']}>
                <HoursAdminPage />
              </PrivateRoute>
            }
          />
          
          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <SettingsPage />
              </PrivateRoute>
            }
          />

          {/* Rota 404 - redireciona para dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </React.Suspense>
    </BrowserRouter>
  );
};

export default AppRoutes;
